import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TooltipProvider } from '@/components/ui/tooltip'
import { BarChart3, XCircle, Loader2 } from 'lucide-react'
import { getInstagramUsernameError, getAnalyticsDaysError } from '@/utils/validation'

interface RequestFormProps {
  targetUsername: string
  setTargetUsername: (value: string) => void
  days: number
  setDays: (value: number) => void
  validationError: string
  isSubmitting: boolean
  onSubmit: (e: React.FormEvent) => void
}

// Removed fade-up variants

// Client-side validation (reused, modular)
const validateUsername = (username: string): string => getInstagramUsernameError(username, { allowEmpty: true })
const validateDays = (days: number): string => getAnalyticsDaysError(days, { allowZeroAsEmpty: true })

export function RequestForm({
  targetUsername,
  setTargetUsername,
  days,
  setDays,
  validationError,
  isSubmitting,
  onSubmit
}: RequestFormProps) {
  const usernameError = validateUsername(targetUsername)
  const daysError = validateDays(days)
  const hasClientErrors = Boolean(usernameError || daysError)

  return (
    <TooltipProvider>
      <div>
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                <BarChart3 className="h-4 w-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold">Create Analytics Request</CardTitle>
                <CardDescription className="text-sm mt-1">
                  Enter Instagram username to analyze
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="target">Instagram Username</Label>
                  <Input
                    id="target"
                    value={targetUsername}
                    onChange={(e) => setTargetUsername(e.target.value)}
                    placeholder="username"
                    className={usernameError || validationError ? 'border-destructive focus-visible:ring-destructive' : ''}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Username must be 1-30 characters and contain only letters, numbers, periods, and underscores
                  </p>
                  {usernameError && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm text-destructive flex items-center gap-1"
                    >
                      <XCircle className="h-3 w-3" />
                      {usernameError}
                    </motion.p>
                  )}
                  {validationError && !usernameError && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm text-destructive flex items-center gap-1"
                    >
                      <XCircle className="h-3 w-3" />
                      {validationError}
                    </motion.p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="days">Days to Analyze (1-60)</Label>
                  <Input
                    id="days"
                    type="number"
                    min="1"
                    max="60"
                    value={days === 0 ? '' : days}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value === '') {
                        setDays(0)
                      } else {
                        const parsed = parseInt(value)
                        setDays(isNaN(parsed) ? 0 : parsed)
                      }
                    }}
                    placeholder="7"
                    className={`${daysError ? 'border-destructive focus-visible:ring-destructive' : ''} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Number of days to look back for posts (1-60)
                  </p>
                  {daysError && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm text-destructive flex items-center gap-1"
                    >
                      <XCircle className="h-3 w-3" />
                      {daysError}
                    </motion.p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                                  <Button 
                    type="submit" 
                    disabled={isSubmitting || !targetUsername.trim() || days <= 0 || hasClientErrors} 
                    className="flex-1"
                  >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating Request...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="mr-2 h-4 w-4" />
                      Create Analytics Request
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  )
} 